import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { CommandExecutionContext } from '../../../types/commands';
import fetchService from '../../../services/fetchService';
import { API_BASE_URL } from '../../../api/config';
import { schemaToParameters } from '../../../utils/schemaToParameters';

export const contacts_update: SimpleDocCommand = {
  id: 'contacts_update',
  logicalName: 'contacts_update',
  version: 'v1',

  description: `Update any field on an existing contact — tier, notes, tags, email, phone, company, title, etc.

**EXAMPLE:**
{ "contactId": "contact_abc123", "tier": "close", "notes": "Met at conference" }

**RETURNS:** Updated contact entity.`,

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        contactId: { type: 'string', description: 'llmId of the contact to update. Required.' },
        name:      { type: 'string', description: 'Update display name' },
        email:     { type: 'string', description: 'Update email address' },
        phone:     { type: 'string', description: 'Update phone number' },
        company:   { type: 'string', description: 'Update company or organization' },
        title:     { type: 'string', description: 'Update job title or role' },
        tier: {
          type: 'string',
          description: 'Update relationship tier',
          enum: ['close', 'regular', 'archived'],
        },
        notes:     { type: 'string', description: 'Update or append notes' },
        tags:      { type: 'array', items: { type: 'string' }, description: 'Replace tags array' },
        linkedinUrl: { type: 'string', description: 'LinkedIn profile URL' },
        lastInteraction: {
          type: 'object',
          description: 'Most recent interaction — set after scanning email, calendar, Reddit, etc.',
          properties: {
            at:      { type: 'string', description: 'ISO date string' },
            channel: { type: 'string', description: 'email | calendar | linkedin | reddit | other' },
            snippet: { type: 'string', description: 'One-sentence summary of the interaction' },
          },
          required: ['at', 'channel'],
        },
        topics:  { type: 'array', items: { type: 'string' }, description: 'Topics of interest extracted from interactions' },
        sources: { type: 'array', items: { type: 'string' }, description: "Data sources where this contact was seen, e.g. ['gmail','linkedin','reddit']" },
        addSignal: {
          type: 'object',
          description: 'Append a new signal to the contact (appends rather than replacing). Use for job changes, notable emails, meetings, etc.',
          properties: {
            type:   { type: 'string', description: 'job_change | email_activity | calendar_meeting | linkedin_post | custom' },
            title:  { type: 'string', description: 'Short title for the signal' },
            body:   { type: 'string', description: 'Optional longer description' },
            source: { type: 'string', description: 'Where the signal came from (e.g. gmail, linkedin, manual)' },
          },
          required: ['type', 'title', 'source'],
        },
      },
      required: ['contactId'],
    }),
  },

  handler: async (params: any, context: CommandExecutionContext) => {
    const { contactId, addSignal, ...fields } = params;

    if (!contactId) {
      return { status: 'error', message: 'contactId is required', format: 'plaintext' as const };
    }

    const updatePayload: Record<string, any> = {};
    const FIELDS = ['name', 'tier', 'email', 'phone', 'company', 'title', 'notes',
                    'tags', 'preferredChannel', 'nextReachOutAt', 'nextReachOutReason',
                    'linkedinUrl', 'twitterHandle', 'githubHandle',
                    'handles', 'identityType', 'primaryChannel', 'primaryChannelContext',
                    'relatedContactIds'];

    for (const key of FIELDS) {
      if (fields[key] !== undefined) updatePayload[key] = fields[key];
    }

    if (addSignal) {
      const current = await fetchService(`${API_BASE_URL}/contacts-app/${contactId}`);
      const existing = current?.skeleton?.signals || [];
      updatePayload.signals = [...existing, {
        id: `signal_${Date.now()}`,
        type: addSignal.type,
        title: addSignal.title,
        body: addSignal.body,
        detectedAt: new Date().toISOString(),
        source: addSignal.source,
      }];
    }

    const updated = await fetchService(`${API_BASE_URL}/contacts-app/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(updatePayload),
    });

    return {
      status: 'success' as const,
      message: `Updated contact: ${updated.name}`,
      data: { contactId: updated._id, llmId: updated.llmId, name: updated.name },
      format: 'json' as const,
    };
  },
};
